import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  ArrowLeft, 
  Trophy
} from 'lucide-react';
import axios from 'axios';
import TestWelcome from './TestWelcome';
import ExamTimer from './ExamTimer';
import ExistingTestModal from './ExistingTestModal';

interface Question {
  _id: string;
  questionNumber: number;
  level: 'Pre-A1' | 'A1' | 'A2';
  questionText: string;
  options: string[];
  correctAnswer: string;
  points: number;
  imageUrl?: string;
}

interface ExamData {
  'Pre-A1': Question[];
  'A1': Question[];
  'A2': Question[];
}

const PlacementTest: React.FC = () => {
  const navigate = useNavigate();
  const [exam, setExam] = useState<ExamData | null>(null);
  const [currentLevel, setCurrentLevel] = useState<'Pre-A1' | 'A1' | 'A2'>('Pre-A1');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [testStarted, setTestStarted] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [showExistingTestModal, setShowExistingTestModal] = useState(false);
  const [existingResults, setExistingResults] = useState<any>(null);
  const [completedLevels, setCompletedLevels] = useState<Set<string>>(new Set());
  const [examModel, setExamModel] = useState<string>('Test 1');

  const startPlacementTest = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setStudentName(user.nombre || user.firstName || 'Estudiante');
      
      console.log('Iniciando solicitud de examen...');
      console.log('Token:', token ? 'Presente' : 'Ausente');
      
      const response = await axios.post('/placement/start', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Respuesta del servidor:', response.data);
      
      // Mapear datos del servidor a los nuevos nombres si es necesario
      const examData = response.data.exam;
      setExamModel(response.data.model || 'Test 1');
      console.log('Datos del examen recibidos:', examData);
      
      const mappedExam = {
        'Pre-A1': examData['Pre-A1'] || examData.starters || [],
        'A1': examData['A1'] || examData.movers || [],
        'A2': examData['A2'] || examData.flyers || []
      };
      
      console.log('Examen mapeado:', mappedExam);
      console.log('Total preguntas:', {
        'Pre-A1': mappedExam['Pre-A1'].length,
        'A1': mappedExam['A1'].length,
        'A2': mappedExam['A2'].length
      });
      
      setExam(mappedExam);
    } catch (error: any) {
      console.error('Error starting placement test:', error);
      console.error('Error details:', error.response?.data);
      if (error.response?.status === 400 && error.response?.data?.existingResult) {
        // Ya tiene un resultado de ubicación - mostrar modal
        setExistingResults(error.response.data.existingResult);
        setShowExistingTestModal(true);
        setLoading(false);
        return;
      }
      const errorMessage = error.response?.data?.error || 'Error iniciando el examen de ubicación';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    startPlacementTest();
  }, [startPlacementTest]);

  const handleStartTest = () => {
    setShowWelcome(false);
    setTestStarted(true);
  };

  const handleTimeUp = () => {
    // Auto-submit cuando se acabe el tiempo
    handleFinishTest();
  };


  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNextQuestion = () => {
    const questions = getCurrentLevelQuestions();
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Verificar si todas las preguntas del nivel actual están respondidas
      const allQuestionsAnswered = questions.every(q => answers[q._id] !== undefined && answers[q._id] !== '');
      
      if (allQuestionsAnswered) {
        // Marcar nivel como completado
        setCompletedLevels(prev => new Set(prev).add(currentLevel));
        
        // Avanzar al siguiente nivel si existe
        if (currentLevel === 'Pre-A1') {
          setCurrentLevel('A1');
          setCurrentQuestionIndex(0);
        } else if (currentLevel === 'A1') {
          setCurrentLevel('A2');
          setCurrentQuestionIndex(0);
        } else {
          // Finalizar examen
          handleFinishTest();
        }
      } else {
        // Mostrar mensaje si faltan preguntas por responder
        alert('Por favor responde todas las preguntas de este nivel antes de continuar.');
      }
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (currentLevel === 'A1') {
      setCurrentLevel('Pre-A1');
      setCurrentQuestionIndex(getCurrentLevelQuestions().length - 1);
    } else if (currentLevel === 'A2') {
      setCurrentLevel('A1');
      setCurrentQuestionIndex(getCurrentLevelQuestions().length - 1);
    }
  };

  const handleFinishTest = async () => {
    try {
      setIsSubmitting(true);
      
      // Preparar respuestas para enviar
      const answersArray = Object.entries(answers).map(([questionId, userAnswer]) => ({
        questionId,
        userAnswer: userAnswer || '' // Asegurar que siempre hay un valor (aunque sea vacío)
      }));

      console.log('📤 Enviando respuestas:', {
        totalAnswers: answersArray.length,
        answers: answersArray,
        testModel: examModel
      });

      // Finish placement test
      const token = localStorage.getItem('token');
      const response = await axios.post('/placement/finish', 
        { answers: answersArray, testModel: examModel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Respuesta del servidor:', response.data);
      
      // Navigate to results
      navigate('/placement/results', { 
        state: { results: response.data.results } 
      });
    } catch (error: any) {
      console.error('❌ Error finishing placement test:', error);
      console.error('❌ Detalles del error:', error.response?.data);
      setError('Error finalizando el examen');
      alert('Error al finalizar el examen. Por favor, intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentLevelQuestions = () => {
    if (!exam) return [];
    return exam[currentLevel];
  };

  const getCurrentQuestion = () => {
    const questions = getCurrentLevelQuestions();
    return questions[currentQuestionIndex];
  };


  // Función para obtener iconos de pregunta según el contenido
  // Función para verificar si un nivel está desbloqueado
  const isLevelUnlocked = (level: string): boolean => {
    if (level === 'Pre-A1') return true; // El primer nivel siempre está desbloqueado
    if (level === 'A1') return completedLevels.has('Pre-A1');
    if (level === 'A2') return completedLevels.has('A1') && completedLevels.has('Pre-A1');
    return false;
  };

  // Verificar completitud cuando cambian las respuestas o el nivel
  React.useEffect(() => {
    if (testStarted && exam) {
      const questions = getCurrentLevelQuestions();
      const totalQuestions = questions.length;
      const answeredQuestions = questions.filter(q => answers[q._id] !== undefined && answers[q._id] !== '').length;
      
      if (answeredQuestions === totalQuestions && totalQuestions > 0) {
        setCompletedLevels(prev => new Set(prev).add(currentLevel));
      }
    }
  }, [answers, currentLevel, testStarted, exam]);

  const getQuestionIcon = () => {
    if (!currentQuestion || !currentQuestion.questionText) {
      return '📝'; // Icono por defecto
    }
    
    const questionText = currentQuestion.questionText.toLowerCase();
    
    // Ocultar icono para preguntas específicas
    if (questionText.includes('what do you wear on your feet') || questionText.includes('wear on your feet')) {
      return null; // Sin icono
    }
    if (questionText.includes("what's the opposite of 'hot'") || questionText.includes("opposite of 'hot'") || 
        (questionText.includes('opposite') && questionText.includes('hot') && !questionText.includes('cold'))) {
      return null; // Sin icono
    }
    if (questionText.includes('where were you yesterday')) {
      return null; // Sin icono
    }
    // Ocultar icono para "I don't know what"
    if (questionText.includes("i don't know what") || questionText.includes("i dont know what") || 
        questionText.includes("don't know what") || questionText.includes("dont know what")) {
      return null; // Sin icono
    }
    
    // Si es la pregunta 1, usar la imagen personalizada
    if (currentQuestion.questionNumber === 1) {
      return (
        <img
          src="https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/pencil.png"
          alt="Pregunta 1"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }
    // Si es la pregunta 2, usar la imagen de sol
    if (currentQuestion.questionNumber === 2) {
      return (
        <img
          src="https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/sun.png"
          alt="Pregunta 2"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }
    // Si es la pregunta 3, usar la imagen de gato debajo de la mesa
    if (currentQuestion.questionNumber === 3) {
      return (
        <img
          src="https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/cat+under+the+table.png"
          alt="Pregunta 3"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }

    // Pregunta con tres manzanas
    if (questionText.includes('three apples') || questionText.includes('3 apples') || questionText.includes('🍎')) {
      return (
        <img
          src="https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/three+apples.png"
          alt="Three apples"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }

    // Pregunta del balón de fútbol
    if (questionText.includes('what can you do with a ball') || questionText.includes('⚽')) {
      return (
        <img
          src="https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/soccer+ball.png"
          alt="Soccer ball"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }

    // Pregunta: "My father goes to work every day" → icono temático padre + trabajo
    if (
      questionText.includes('my father goes to work every day') ||
      (questionText.includes('father') && questionText.includes('work'))
    ) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            fontSize: '72px',
            width: '100%',
            height: '100%'
          }}
        >
          <span aria-label="father" role="img">👨‍👔</span>
          <span aria-label="office" role="img">🏢</span>
        </div>
      );
    }

    // Pregunta de la hora
    if (questionText.includes('what time is it') || questionText.includes('🕒')) {
      return (
        <img
          src="https://d3ihltymm97iyi.cloudfront.net/childrentest/Test+1/three+o_clock.png"
          alt="Three o'clock"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }

    // Pregunta específica: mostrar solo un cuadro azul sin número
    if (questionText.includes('which one is a color')) {
      return (
        <div
          style={{
            width: '140px',
            height: '140px',
            background: '#4da3ff',
            borderRadius: '20px',
            boxShadow: 'inset 0 8px 14px rgba(255,255,255,0.25), inset 0 -8px 14px rgba(0,0,0,0.15)',
            margin: '0 auto'
          }}
        />
      );
    }

    // Pregunta específica: mostrar cuadro azul con el número 10
    if (questionText.includes('which one is a number')) {
      return (
        <div
          style={{
            width: '140px',
            height: '140px',
            background: '#4da3ff',
            borderRadius: '20px',
            boxShadow: 'inset 0 8px 14px rgba(255,255,255,0.25), inset 0 -8px 14px rgba(0,0,0,0.15)',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '60px',
            fontWeight: 'bold',
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
          }}
        >
          10
        </div>
      );
    }

    // Pregunta específica: "Which one is food?" → mostrar icono de pan
    if (questionText.includes('which one is food')) {
      return '🍞';
    }
    
    // Iconos específicos según el contenido de la pregunta - más grandes y específicos para niños
    if (questionText.includes('pen') || questionText.includes('🖊️')) {
      return '🖊️';
    } else if (questionText.includes('sun') || questionText.includes('🌞')) {
      return '☀️';
    } else if (questionText.includes('cat') || questionText.includes('🐱')) {
      return '🐱';
    } else if (questionText.includes('dog') || questionText.includes('🐶')) {
      return '🐶';
    } else if (questionText.includes('car') || questionText.includes('🚗')) {
      return '🚗';
    } else if (questionText.includes('book') || questionText.includes('📚')) {
      return '📚';
    } else if (questionText.includes('apple') || questionText.includes('🍎')) {
      return '🍎';
    } else if (questionText.includes('ball') || questionText.includes('⚽')) {
      return '⚽';
    } else if (questionText.includes('house') || questionText.includes('🏠')) {
      return '🏠';
    } else if (questionText.includes('tree') || questionText.includes('🌳')) {
      return '🌳';
    } else if (questionText.includes('water') || questionText.includes('💧')) {
      return '💧';
    } else if (questionText.includes('food') || questionText.includes('🍕')) {
      return '🍕';
    } else if (questionText.includes('pizza') || questionText.includes('🍕')) {
      return '🍕';
    } else if (questionText.includes('milk') || questionText.includes('🥛')) {
      return '🥛';
    } else if (questionText.includes('bread') || questionText.includes('🍞')) {
      return '🍞';
    } else if (questionText.includes('cake') || questionText.includes('🎂')) {
      return '🎂';
    } else if (questionText.includes('fish') || questionText.includes('🐟')) {
      return '🐟';
    } else if (questionText.includes('bird') || questionText.includes('🐦')) {
      return '🐦';
    } else if (questionText.includes('elephant') || questionText.includes('🐘')) {
      return '🐘';
    } else if (questionText.includes('lion') || questionText.includes('🦁')) {
      return '🦁';
    } else if (questionText.includes('bear') || questionText.includes('🐻')) {
      return '🐻';
    } else if (questionText.includes('rabbit') || questionText.includes('🐰')) {
      return '🐰';
    } else if (questionText.includes('monkey') || questionText.includes('🐵')) {
      return '🐵';
    } else if (questionText.includes('horse') || questionText.includes('🐴')) {
      return '🐴';
    } else if (questionText.includes('cow') || questionText.includes('🐄')) {
      return '🐄';
    } else if (questionText.includes('pig') || questionText.includes('🐷')) {
      return '🐷';
    } else if (questionText.includes('duck') || questionText.includes('🦆')) {
      return '🦆';
    } else if (questionText.includes('frog') || questionText.includes('🐸')) {
      return '🐸';
    } else if (questionText.includes('butterfly') || questionText.includes('🦋')) {
      return '🦋';
    } else if (questionText.includes('bee') || questionText.includes('🐝')) {
      return '🐝';
    } else if (questionText.includes('spider') || questionText.includes('🕷️')) {
      return '🕷️';
    } else if (questionText.includes('ant') || questionText.includes('🐜')) {
      return '🐜';
    } else if (questionText.includes('flower') || questionText.includes('🌸')) {
      return '🌸';
    } else if (questionText.includes('star') || questionText.includes('⭐')) {
      return '⭐';
    } else if (questionText.includes('moon') || questionText.includes('🌙')) {
      return '🌙';
    } else if (questionText.includes('cloud') || questionText.includes('☁️')) {
      return '☁️';
    } else if (questionText.includes('rain') || questionText.includes('🌧️')) {
      return '🌧️';
    } else if (questionText.includes('snow') || questionText.includes('❄️')) {
      return '❄️';
    } else if (questionText.includes('fire') || questionText.includes('🔥')) {
      return '🔥';
    } else if (questionText.includes('heart') || questionText.includes('❤️')) {
      return '❤️';
    } else if (questionText.includes('smile') || questionText.includes('😊')) {
      return '😊';
    } else if (questionText.includes('cry') || questionText.includes('😢')) {
      return '😢';
    } else if (questionText.includes('angry') || questionText.includes('😠')) {
      return '😠';
    } else if (questionText.includes('happy') || questionText.includes('😄')) {
      return '😄';
    } else if (questionText.includes('sad') || questionText.includes('😞')) {
      return '😞';
    } else if (questionText.includes('tired') || questionText.includes('😴')) {
      return '😴';
    } else if (questionText.includes('hungry') || questionText.includes('😋')) {
      return '😋';
    } else if (questionText.includes('thirsty') || questionText.includes('🥤')) {
      return '🥤';
    } else if (questionText.includes('hot') || questionText.includes('🌡️')) {
      return '🌡️';
    } else if (questionText.includes('cold') || questionText.includes('🧊')) {
      return '🧊';
    } else if (questionText.includes('big') || questionText.includes('🔍')) {
      return '🔍';
    } else if (questionText.includes('small') || questionText.includes('🔬')) {
      return '🔬';
    } else if (questionText.includes('tall') || questionText.includes('📏')) {
      return '📏';
    } else if (questionText.includes('short') || questionText.includes('📐')) {
      return '📐';
    } else if (questionText.includes('fast') || questionText.includes('🏃')) {
      return '🏃';
    } else if (questionText.includes('slow') || questionText.includes('🐌')) {
      return '🐌';
    } else if (questionText.includes('loud') || questionText.includes('📢')) {
      return '📢';
    } else if (questionText.includes('quiet') || questionText.includes('🤫')) {
      return '🤫';
    } else if (questionText.includes('red') || questionText.includes('🔴')) {
      return '🔴';
    } else if (questionText.includes('blue') || questionText.includes('🔵')) {
      return '🔵';
    } else if (questionText.includes('green') || questionText.includes('🟢')) {
      return '🟢';
    } else if (questionText.includes('yellow') || questionText.includes('🟡')) {
      return '🟡';
    } else if (questionText.includes('orange') || questionText.includes('🟠')) {
      return '🟠';
    } else if (questionText.includes('purple') || questionText.includes('🟣')) {
      return '🟣';
    } else if (questionText.includes('pink') || questionText.includes('🩷')) {
      return '🩷';
    } else if (questionText.includes('brown') || questionText.includes('🤎')) {
      return '🤎';
    } else if (questionText.includes('black') || questionText.includes('⚫')) {
      return '⚫';
    } else if (questionText.includes('white') || questionText.includes('⚪')) {
      return '⚪';
    } else if (questionText.includes('gray') || questionText.includes('🔘')) {
      return '🔘';
    } else if (questionText.includes('one') || questionText.includes('1')) {
      return '1️⃣';
    } else if (questionText.includes('two') || questionText.includes('2')) {
      return '2️⃣';
    } else if (questionText.includes('three') || questionText.includes('3')) {
      return '3️⃣';
    } else if (questionText.includes('four') || questionText.includes('4')) {
      return '4️⃣';
    } else if (questionText.includes('five') || questionText.includes('5')) {
      return '5️⃣';
    } else if (questionText.includes('six') || questionText.includes('6')) {
      return '6️⃣';
    } else if (questionText.includes('seven') || questionText.includes('7')) {
      return '7️⃣';
    } else if (questionText.includes('eight') || questionText.includes('8')) {
      return '8️⃣';
    } else if (questionText.includes('nine') || questionText.includes('9')) {
      return '9️⃣';
    } else if (questionText.includes('ten') || questionText.includes('10')) {
      return '🔟';
    } else if (questionText.includes('what is this') || questionText.includes('qué es esto')) {
      return '🤔';
    } else if (questionText.includes('choose') || questionText.includes('elige')) {
      return '🎯';
    } else if (questionText.includes('where') || questionText.includes('dónde')) {
      return '📍';
    } else if (questionText.includes('which') || questionText.includes('cuál')) {
      return '❓';
    } else if (questionText.includes('how many') || questionText.includes('cuántos')) {
      return '🔢';
    } else if (questionText.includes('what can you do') || questionText.includes('qué puedes hacer')) {
      return '🎪';
    } else if (questionText.includes('opposite') || questionText.includes('opuesto')) {
      return '🔄';
    } else if (questionText.includes('time') || questionText.includes('hora')) {
      return '🕒';
    } else if (questionText.includes('sentence') || questionText.includes('oración')) {
      return '📝';
    } else if (questionText.includes('fruit') || questionText.includes('fruta')) {
      return '🍎';
    } else if (questionText.includes('yesterday') || questionText.includes('ayer')) {
      return '📅';
    } else if (questionText.includes('if') || questionText.includes('si')) {
      return '🤷';
    } else if (questionText.includes('never') || questionText.includes('nunca')) {
      return '❌';
    } else if (questionText.includes('tallest') || questionText.includes('más alto')) {
      return '📏';
    } else if (questionText.includes('child') || questionText.includes('niño')) {
      return '👶';
    } else if (questionText.includes('word') || questionText.includes('palabra')) {
      return '📖';
    } else {
      // Iconos por defecto según el nivel - más grandes y coloridos
      const defaultIcons = {
        'Pre-A1': ['🤔', '🧐', '🤓', '👶', '🎈', '🎨', '🌈'],
        'A1': ['🤔', '🧐', '🤓', '👦', '🎯', '🎪', '🎨'],
        'A2': ['🤔', '🧐', '🤓', '👧', '🎪', '🎨', '🎭']
      };
      const icons = defaultIcons[currentLevel] || ['🤔', '🧐', '🤓'];
      return icons[currentQuestionIndex % icons.length];
    }
  };

  const getLevelInfo = (level: string) => {
    switch (level) {
      case 'Pre-A1':
        return { name: 'Pre-A1', color: '#ff6b6b', emoji: '🚀', description: 'Vocabulario básico, colores, números' };
      case 'A1':
        return { name: 'A1', color: '#4ecdc4', emoji: '🎯', description: 'Presente simple, pasado simple, rutinas' };
      case 'A2':
        return { name: 'A2', color: '#45b7d1', emoji: '🌟', description: 'Comparativos, condicionales, presente perfecto' };
      default:
        return { name: '', color: '#666', emoji: '', description: '' };
    }
  };

  const getProgress = () => {
    const totalQuestions = (exam?.['Pre-A1'].length || 0) + (exam?.A1.length || 0) + (exam?.A2.length || 0);
    let completedQuestions = 0;
    
    if (currentLevel === 'Pre-A1') {
      completedQuestions = currentQuestionIndex;
    } else if (currentLevel === 'A1') {
      completedQuestions = (exam?.['Pre-A1'].length || 0) + currentQuestionIndex;
    } else if (currentLevel === 'A2') {
      completedQuestions = (exam?.['Pre-A1'].length || 0) + (exam?.A1.length || 0) + currentQuestionIndex;
    }
    
    return Math.round((completedQuestions / totalQuestions) * 100);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: 'white', fontSize: '18px' }}>
          Preparando tu examen de ubicación... 🎯
        </p>
      </div>
    );
  }

  // Verificar que el examen esté cargado y tenga datos
  if (error || !exam || !exam[currentLevel] || exam[currentLevel].length === 0) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', maxWidth: '500px', margin: '50px auto' }}>
          <h2>😔 Error</h2>
          <p>{error || 'Examen de ubicación no encontrado'}</p>
          <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Mostrar página de bienvenida
  if (showWelcome) {
    const testDetails = {
      duration: 30,
      totalQuestions: 20,
      attempts: 1,
      deadline: 'Sin fecha límite'
    };

    return (
      <TestWelcome
        studentName={studentName}
        onStartTest={handleStartTest}
        testDetails={testDetails}
      />
    );
  }

  const currentQuestion = getCurrentQuestion();
  const levelInfo = getLevelInfo(currentLevel);
  const progress = getProgress();
  const displayedQuestionText = (() => {
    if (!currentQuestion) return '';
    const base = (currentQuestion.questionText || '');
    // Remover emojis específicos del texto visible
    return base
      .replace(/🖊️|🌞|🐱|🍎|⚽|🕒/g, '')
      .replace('(The cat is under the table)', '')
      .trim();
  })();

  const shouldShowImage = (() => {
    if (!currentQuestion) return true;
    const qt = (currentQuestion.questionText || '').toLowerCase();
    
    // Primero verificar condiciones que ocultan la imagen (tienen prioridad)
    if (currentQuestion.questionNumber === 10) return false;
    if (qt.includes('which sentence is correct')) return false;
    if (qt.includes('where are you from')) return false;
    // Ocultar icono para preguntas específicas
    if (qt.includes('what do you wear on your feet') || qt.includes('wear on your feet')) return false;
    if (qt.includes("what's the opposite of 'hot'") || qt.includes("opposite of 'hot'") || 
        (qt.includes('opposite') && qt.includes('hot') && !qt.includes('cold'))) return false;
    if (qt.includes('where were you yesterday')) return false;
    const noImagePhrases = [
      "i don't know what",
      "i dont know what",
      "don't know what",
      "dont know what",
      'yesterday we',
      'which one is not a fruit',
      'which one is a school subject',
      'what do you use to write',
      'i have lived here',
      'i have lived in this city',
      'she ______ breakfast',
      'this film is',
      'he asked me if i',
      'while i ______ tv',
      'while i ______ television',
      'while they ______ dinner',
      'if i ______ enough money',
      'she is the ______ girl',
      "what's the opposite of \"young\"",
      "whats the opposite of \"young\"",
      'opposite of "young"',
      'opposite of \'young\'',
      'how are you today',
      'if it',
      'choose the correct word: "he is the',
      'choose the correct word: "he is the',
      'he is the ______ person in the class',
      'when i was a child, i',
      'to the beach every summer',
      "what's the best ending",
      'whats the best ending',
      'choose the correct question',
      'what color is the sky',
      'she ______ to music every afternoon',
      'my father ______ to work every day',
      'he ______ a bike every day',
      'which one is a day of the week',
      "what's the opposite of \"happy\"",
      'choose the odd one out',
      'how do you go to school',
      'what do you usually eat for breakfast',
      'what did they do last weekend',
      'where is your school',
      'what did he do last night',
      'which one is correct',
      'could you tell me where',
      'tell me where'
    ];
    if (noImagePhrases.some(p => qt.includes(p))) return false;
    
    // Si la pregunta tiene imageUrl configurada, mostrar la imagen
    if (currentQuestion.imageUrl) return true;
    
    return true;
  })();

  // Verificar que tenemos una pregunta válida antes de renderizar
  if (!currentQuestion || loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
          <p>Cargando examen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header con progreso y reloj */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="card"
        style={{ marginBottom: '20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h2 style={{ color: '#333', margin: 0 }}>
              🎯 Test de Ubicación de Nivel
            </h2>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>
              {levelInfo.emoji} {levelInfo.name} - Pregunta {currentQuestionIndex + 1} de {getCurrentLevelQuestions().length}
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Reloj del examen */}
            <ExamTimer
              duration={30}
              onTimeUp={handleTimeUp}
              isRunning={testStarted}
            />
            
            <div style={{ 
              background: levelInfo.color, 
              color: 'white', 
              padding: '10px 20px', 
              borderRadius: '25px', 
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {progress}% Completado
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ 
          width: '100%', 
          height: '8px', 
          background: 'rgba(0,0,0,0.1)', 
          borderRadius: '4px', 
          marginTop: '15px',
          overflow: 'hidden'
        }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
            style={{
              height: '100%',
              background: `linear-gradient(45deg, ${levelInfo.color}, #4ecdc4)`,
              borderRadius: '4px'
            }}
          />
        </div>
      </motion.div>

      {/* Información del nivel actual */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card"
        style={{ 
          background: `linear-gradient(45deg, ${levelInfo.color}20, #4ecdc420)`,
          border: `2px solid ${levelInfo.color}`,
          marginBottom: '20px'
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ color: levelInfo.color, marginBottom: '10px', fontSize: '28px' }}>
            {levelInfo.emoji} {levelInfo.name}
          </h3>
          <p style={{ color: '#333', margin: 0, fontSize: '22px', fontWeight: '600' }}>
            {levelInfo.description}
          </p>
        </div>
      </motion.div>

      {/* Pregunta actual */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${currentLevel}-${currentQuestionIndex}`}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          className="card"
        >
          <div style={{ marginBottom: '30px' }}>
            {/* Layout horizontal: imagen a la izquierda, pregunta y opciones a la derecha */}
            <div
              className="placement-question-layout"
              style={shouldShowImage ? undefined : { gridTemplateColumns: '1fr', justifyItems: 'center', minHeight: 'auto' }}
            >
              {/* Lado izquierdo: Imagen grande */}
              {shouldShowImage && (
              <div className="placement-image-container">
                <div className="placement-image">
                  {currentQuestion.imageUrl ? (
                    <img src={currentQuestion.imageUrl} alt="Illustration" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    getQuestionIcon()
                  )}
                </div>
              </div>
              )}

              {/* Lado derecho: Pregunta y opciones */}
              <div className="placement-options-container" style={shouldShowImage ? undefined : { width: '100%' }}>
                {/* Pregunta */}
                <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                  <h3 className="placement-question-text">
              {displayedQuestionText}
            </h3>
                </div>

            {/* Opciones de respuesta */}
                <div className="placement-options-grid">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  className={`option ${
                    answers[currentQuestion._id] === option ? 'selected' : ''
                  }`}
                  onClick={() => handleAnswerChange(currentQuestion._id, option)}
                      style={{
                        fontSize: '26px',
                        padding: '20px 25px',
                        fontWeight: '600',
                        minHeight: '75px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }}
                    >
                      <span style={{ marginRight: '15px', fontWeight: 'bold', fontSize: '32px' }}>
                    {String.fromCharCode(65 + index)}.
                  </span>
                  {option}
                </button>
              ))}
                </div>
              </div>
            </div>
          </div>

          {/* Navegación */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <button
              onClick={handlePreviousQuestion}
              disabled={currentLevel === 'Pre-A1' && currentQuestionIndex === 0}
              className="btn btn-secondary"
              style={{ 
                opacity: (currentLevel === 'Pre-A1' && currentQuestionIndex === 0) ? 0.5 : 1,
                cursor: (currentLevel === 'Pre-A1' && currentQuestionIndex === 0) ? 'not-allowed' : 'pointer',
                fontSize: '18px',
                padding: '15px 25px',
                fontWeight: 'bold'
              }}
            >
              <ArrowLeft style={{ marginRight: '10px', fontSize: '20px' }} />
              ANTERIOR
            </button>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ color: '#333', fontSize: '18px', fontWeight: 'bold' }}>
                {answers[currentQuestion._id] ? '✅ Respondida' : '❓ Sin responder'}
              </span>
            </div>

            {currentLevel === 'A2' && currentQuestionIndex === getCurrentLevelQuestions().length - 1 ? (
              <button
                onClick={handleFinishTest}
                disabled={isSubmitting}
                className="btn btn-success"
                style={{
                  fontSize: '18px',
                  padding: '15px 25px',
                  fontWeight: 'bold'
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner" style={{ 
                      width: '20px', 
                      height: '20px', 
                      marginRight: '10px',
                      display: 'inline-block'
                    }}></div>
                    Finalizando...
                  </>
                ) : (
                  <>
                    <Trophy style={{ marginRight: '10px', fontSize: '20px' }} />
                    🏆 FINALIZAR
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="btn"
                style={{ 
                  background: `linear-gradient(45deg, ${levelInfo.color}, #4ecdc4)`,
                  fontSize: '18px',
                  padding: '15px 25px',
                  fontWeight: 'bold'
                }}
              >
                SIGUIENTE
                <ArrowRight style={{ marginLeft: '10px', fontSize: '20px' }} />
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navegación rápida por niveles */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <h4 style={{ color: '#333', marginBottom: '15px', textAlign: 'center' }}>
          📋 Niveles del Test
        </h4>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px' 
        }}>
          {[
            { level: 'Pre-A1', name: 'Pre-A1', emoji: '🚀', color: '#ff6b6b' },
            { level: 'A1', name: 'A1', emoji: '🎯', color: '#4ecdc4' },
            { level: 'A2', name: 'A2', emoji: '🌟', color: '#45b7d1' }
          ].map(({ level, name, emoji, color }) => {
            const isUnlocked = isLevelUnlocked(level);
            const isActive = currentLevel === level;
            
            return (
              <div
                key={level}
                style={{
                  background: isActive ? color : isUnlocked ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.1)',
                  color: isActive ? 'white' : isUnlocked ? '#333' : '#999',
                  padding: '15px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  cursor: isUnlocked ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                  border: `2px solid ${isActive ? color : 'transparent'}`,
                  position: 'relative',
                  opacity: isUnlocked ? 1 : 0.5
                }}
                onClick={() => {
                  if (isUnlocked) {
                    setCurrentLevel(level as any);
                    setCurrentQuestionIndex(0);
                  }
                }}
              >
                {!isUnlocked && !isActive && (
                  <div style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '25px',
                    height: '25px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px'
                  }}>
                    🔒
                  </div>
                )}
                <div style={{ fontSize: '2rem', marginBottom: '5px' }}>{emoji}</div>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{name}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  {level === 'Pre-A1' ? '7 preguntas' :
                   level === 'A1' ? '7 preguntas' : '6 preguntas'}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Elementos decorativos */}
      <motion.div
        animate={{ 
          y: [0, -15, 0],
          rotate: [0, 5, 0]
        }}
        transition={{ 
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          fontSize: '2rem',
          opacity: 0.6
        }}
      >
        🎯
      </motion.div>

      <motion.div
        animate={{ 
          y: [0, 20, 0],
          rotate: [0, -5, 0]
        }}
        transition={{ 
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          fontSize: '2rem',
          opacity: 0.7
        }}
      >
        📚
      </motion.div>

      {/* Modal para test existente */}
      <ExistingTestModal
        isOpen={showExistingTestModal}
        onClose={() => setShowExistingTestModal(false)}
        results={existingResults}
      />
    </div>
  );
};

export default PlacementTest;
